import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { toast } from 'react-toastify'
import { useRest } from '../../../components/Context'
import { ICompanyData } from '../CompanyProfileTabs/interfaces'
import { Loader } from '../../../components/Loader'
import { columns } from './companyDetailsOfUsersList'
import { TableComponent } from '../../../components/Table'
import { generateParams } from '../../../utils'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(4),
    padding: theme.spacing(4),
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    color: theme.palette.primary.light,
    paddingBottom: theme.spacing(3),
    '&>span:nth-child(1)': {
      opacity: '0.72',
      paddingRight: theme.spacing(1.5),
    },
    '&>span:nth-child(2)': {
      opacity: 1,
    },
  },
}))

interface ICompanyDetails {
  data?: ICompanyData
}

interface ICompanyDetailsField {
  name: string
  data: string | undefined
}

export const CompanyDetails: FC<ICompanyDetails> = ({ data }) => {
  const classes = useStyles()
  const { rxios } = useRest()
  const [loading, setLoading] = useState<boolean>(true)
  const [values, setValues] = useState<any>({}) // eslint-disable-line

  const formMethods = useForm({
    mode: 'onBlur',
  })

  const formatDate = (value: string) => {
    const newDate = new Date(value)
    return newDate.toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  const CompanyDetailsFields: ICompanyDetailsField[] = [
    {
      name: 'Company Legal Name: ',
      data: data?.name,
    },
    {
      name: 'Phone Number: ',
      data: data?.phone,
    },
    {
      name: 'Company DBA Name: ',
      data: data?.dbaName,
    },
    {
      name: 'Main Point of Contact: ',
      data: data?.personalContact,
    },
    {
      name: 'Address: ',
      data: data?.address,
    },
    {
      name: 'Introduced By: ',
      data: data?.introducedBy,
    },
    {
      name: 'Settlement Contact Info: ',
      data: data?.settlementContact,
    },
    {
      name: 'Introduced Date: ',
      data: data?.introducedDate ? formatDate(data.introducedDate) : '-',
    },
    {
      name: 'Compliance Contact Info: ',
      data: data?.complianceContact,
    },
    {
      name: 'Currency: ',
      data: data?.currency,
    },
  ]

  const loadData = useCallback(
    async (page) => {
      setLoading(true)

      if (data?.id) {
        rxios.get(`/company/${data?.id}/users?${generateParams({ page, count: '10' })}`).subscribe(
          (next) => {
            setValues(next)
            setLoading(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      }
    },
    [data, rxios],
  )

  useEffect(() => {
    ;(async () => {
      await loadData(1)
    })()
    // eslint-disable-next-line
  }, [])

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <FormProvider {...formMethods}>
          <Grid container className={classes.container}>
            <Grid container item xs={12}>
              {CompanyDetailsFields.map((field: ICompanyDetailsField, index) => (
                <Grid item xs={12} sm={6} key={index} className={classes.field}>
                  <Typography component="span">{field.name}</Typography>
                  <Typography component="span">{field.data || '-'}</Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <TableComponent
              columns={columns}
              data={values.items}
              params={values.meta}
              loadData={loadData}
              loading={loading}
              isShowDetailsBtn
              isShowEditIcon={false}
              isShowInfoIcon={false}
            />
          </Grid>
        </FormProvider>
      )}
    </>
  )
}
